import { useQuery } from '@tanstack/react-query';
import { FC, PropsWithChildren, ReactElement } from 'react';
import { type Location, Navigate, RouteObject, useLocation } from 'react-router-dom';

import { _getQuestionnaireStatus } from '@/api/auth/queries';
import { useGetUserProfile } from '@/api/user-dashboard/queries';
import { PostAuthReturnKind } from '@/features/auth/post-auth-return';
import { hasWalletExportQuery } from '@/features/wallet-access/wallet-export-request';
import type { FeatureFlagName } from '@/global-hooks/use-feature-flag-enabled';
import { GeneralLoadingFallback } from '@/shared/modules/general-loading-fallback';
import { Permission, usePermissions } from '@/shared/modules/permissions';
import { UserRole } from '@/stores/slices/selected-user-config';

import { attemptChunkReloadForError, reportChunkLoadError } from './chunk-load-recovery';
import { RouteType } from './data';
import { PagePaths } from './page-paths';
import { RequireFeatureFlag } from './require-feature-flag';
import { RequirePermission } from './require-permission';
import { RequireSession } from './require-session';

const signedOutReturnState = (location: Location) =>
  location.pathname === PagePaths.settings && hasWalletExportQuery(location.search)
    ? { postAuthReturn: { kind: PostAuthReturnKind.WalletExport } }
    : undefined;

const ProtectedRoute: FC<PropsWithChildren> = ({ children }) => {
  const { user, isPending } = useGetUserProfile();
  const location = useLocation();
  const { has, isLoading: permissionsUnresolved } = usePermissions();
  const canActAcrossEntities = has(Permission.GlobalAdminCrossEntityAccess);
  const { data: status, isPending: isStatusPending } = useQuery({
    ..._getQuestionnaireStatus(),
    enabled: !!user && user.hasSubmittedQuestionnaire,
  });

  // Only a resolved absence is a signed-out state. `useCompleteAuthSession` resets the profile
  // query and navigates here in the same tick, so the first render after a sign-in always sees no
  // user yet — redirecting on that sends someone who just authenticated back to the sign-in form.
  if (isPending) {
    return <GeneralLoadingFallback />;
  }

  if (!user) {
    return <Navigate to={PagePaths.root} replace state={signedOutReturnState(location)} />;
  }
  const isAllFundsSection = location.pathname.startsWith(PagePaths.allFunds);
  const isQuestionnairePage = location.pathname.startsWith(PagePaths.questionnaire);
  const hasPendingQuestions = (status?.pendingQuestionIds.length ?? 0) > 0;
  const gateApplies = !permissionsUnresolved && !canActAcrossEntities;

  if (gateApplies && user.hasSubmittedQuestionnaire && !isQuestionnairePage && isStatusPending) {
    return <GeneralLoadingFallback />;
  }

  const mustAnswerQuestionnaire =
    (isAllFundsSection && !user.hasSubmittedQuestionnaire) ||
    (!isQuestionnairePage && hasPendingQuestions);

  if (gateApplies && mustAnswerQuestionnaire) {
    return <Navigate to={PagePaths.questionnaire} replace />;
  }

  return children;
};

export type RouteGuards = {
  /** Require an authenticated session (`ProtectedRoute`). */
  isProtected?: boolean;
  /** Require a specific permission (`RequirePermission`). Absent = any session. */
  permission?: Permission | readonly Permission[];
  /** Feature flag gating this route (`RequireFeatureFlag`). Absent = always released. */
  featureFlag?: FeatureFlagName;
  /** Where a closed `featureFlag` sends the visitor. Defaults to the Funds page. */
  featureFlagRedirectTo?: string;
  /** Require a specific active account session (`RequireSession`). Absent = any. */
  session?: UserRole | readonly UserRole[];
};

/**
 * Wraps a page element in the guards a route asked for, outermost first:
 * authentication, then release, then authorization, then active-session, then
 * active session. A route that asked for none gets its element back untouched.
 *
 * Release sits outside authorization so an unreleased page cannot toast about a
 * missing permission — that would announce a page nobody is meant to know about.
 */
export const withRouteGuards = (
  element: ReactElement,
  { isProtected, permission, session, featureFlag, featureFlagRedirectTo }: RouteGuards,
): ReactElement => {
  const sessionScoped = session ? (
    <RequireSession session={session}>{element}</RequireSession>
  ) : (
    element
  );

  const authorized = permission ? (
    <RequirePermission permission={permission}>{sessionScoped}</RequirePermission>
  ) : (
    sessionScoped
  );

  const released = featureFlag ? (
    <RequireFeatureFlag flag={featureFlag} redirectTo={featureFlagRedirectTo}>
      {authorized}
    </RequireFeatureFlag>
  ) : (
    authorized
  );

  return isProtected ? <ProtectedRoute>{released}</ProtectedRoute> : released;
};

const modules = import.meta.glob('../pages/**/index.tsx');

const loadPageComponent = async (importPath: string): Promise<FC | null> => {
  const importModule = modules[`../pages/${importPath}/index.tsx`] as () => Promise<{
    default: FC;
  }>;
  const context = { source: 'route.lazy', routeImportPath: importPath };

  try {
    return (await importModule()).default;
  } catch (error) {
    if (attemptChunkReloadForError(error, context)) {
      return null;
    }

    reportChunkLoadError(error, context, { action: 'throw' });
    throw error;
  }
};

const createRoute = ({
  importPath,
  path,
  isProtected,
  permission,
  featureFlag,
  featureFlagRedirectTo,
  session,
  ...rest
}: RouteType & { isProtected?: boolean }): RouteObject => ({
  path,
  async lazy() {
    const Component = await loadPageComponent(importPath);

    if (!Component) {
      return { element: null };
    }
    return {
      element: withRouteGuards(<Component />, {
        isProtected,
        permission,
        featureFlag,
        featureFlagRedirectTo,
        session,
      }),
    };
  },
  ...rest,
});

export const generateRoutes = (routes: RouteType[], isProtected?: boolean) => {
  return routes.map((route) => createRoute({ ...route, isProtected }));
};
